import { IVersionedChoice, PropertySheet, PropertySheetCategory, TypedCardElementPeer } from "adaptivecards-designer";
import * as Adaptive from "adaptivecards";
import React from "react";
import Select from 'react-select';
import { createRoot } from 'react-dom/client';
import { Input } from "adaptivecards";
import { GetExpandedInputChoiceSet, handleValidationElement, initCustomRenderedElement, removeOtherPropertyConfigurations, SetTeamsExperiencePropertyConfigurations } from "../Utils/CustomElementUtils";
import { getPropertyEditorInstance, PresentationKeys, PropertyKeys } from "../Pickers/PickerBase";
import { colourStyles } from "./CollaboratorsTypeahead";
import { AdaptiveCardTexts } from "../../../shared/utils/constants";

const displayLabelProperty = new Adaptive.StringProperty(Adaptive.Versions.v1_0, "displayLabel", true);
const displayChoicesProperty = new Adaptive.EnumProperty(Adaptive.Versions.v1_0, "displayChoices", []);
const requireFieldProperty = new Adaptive.BoolProperty(Adaptive.Versions.v1_0, "requireField", false);
const dataSourceProperty = new Adaptive.StringProperty(Adaptive.Versions.v1_0, "dataSource", true, undefined, "0");
const presentationProperty = new Adaptive.StringProperty(Adaptive.Versions.v1_0, "presentation", true);
const isMultiSelectProperty = new Adaptive.BoolProperty(Adaptive.Versions.v1_0, "isMultiSelectField", true);

export class TagsTypeahead extends Adaptive.TextInput {
    static readonly displayLabelProperty = displayLabelProperty;
    static readonly displayChoicesProperty = displayChoicesProperty;
    static readonly requireFieldProperty = requireFieldProperty;
    static readonly dataSourceProperty = dataSourceProperty;
    static readonly presentationProperty = presentationProperty;    
    static readonly isMultiSelectProperty = isMultiSelectProperty;    
    static readonly JsonTypeName = "Tags";
    static readonly ElementId = "Ticket.Tags";
    static readonly defaultLabel = "Tags";
    static pickerChoices: any[];
    static onCustomValueChange: (input: Input) => void;
    static disableField: boolean;
    static presentInTeams: boolean;

    //#region Schema

    @Adaptive.property(displayLabelProperty)
    get displayLabel(): string | undefined {
        return this.getValue(TagsTypeahead.displayLabelProperty);
    }

    set displayLabel(value: string | undefined) {
        this.setValue(TagsTypeahead.displayLabelProperty, value);
        this.label = value;
        this.updateLayout();
    }

    @Adaptive.property(displayChoicesProperty)
    get displayChoices(): any[] | undefined {
        return this.getValue(TagsTypeahead.displayChoicesProperty);
    }

    set displayChoices(value: any[] | undefined, ) {
        this.setValue(TagsTypeahead.displayChoicesProperty, value);
        this.updateLayout();
    }

    @Adaptive.property(requireFieldProperty)
    get requireField(): boolean | undefined {
        return this.getValue(TagsTypeahead.requireFieldProperty);
    }

    set requireField(value: boolean | undefined) {
        this.setValue(TagsTypeahead.requireFieldProperty, value);
        this.setValue(TagsTypeahead.isRequiredProperty, value);
        if(value)
            this.setValue(TagsTypeahead.errorMessageProperty, AdaptiveCardTexts.REQUIRE_FIELD);
        this.updateLayout();
    }

    @Adaptive.property(dataSourceProperty)
    get dataSource(): string | undefined {
        return this.getValue(TagsTypeahead.dataSourceProperty);
    }

    set dataSource(value: string | undefined) {
        this.setValue(TagsTypeahead.dataSourceProperty, value);
        this.updateLayout();
    }

    @Adaptive.property(presentationProperty)
    get presentation(): string | undefined {
        return this.getValue(TagsTypeahead.presentationProperty);
    }

    set presentation(value: string | undefined) {
        this.setValue(TagsTypeahead.presentationProperty, value);
        this.updateLayout();
    }

    @Adaptive.property(isMultiSelectProperty)
    get isMultiSelectField(): boolean | undefined {
        return this.getValue(TagsTypeahead.isMultiSelectProperty);
    }

    set isMultiSelectField(value: boolean | undefined) {
        this.setValue(TagsTypeahead.isMultiSelectProperty, value);
        this.updateLayout();
    }

    //#endregion

    protected internalRender(): HTMLElement | undefined {
        this.id = TagsTypeahead.ElementId;
        if((this?.displayLabel ?? "").length == 0)
            this.displayLabel = TagsTypeahead.defaultLabel;

        if(TagsTypeahead.pickerChoices){
            this.displayChoices = TagsTypeahead.pickerChoices;
        }

        if((this?.presentation ?? "").length == 0)
            this.presentation = PresentationKeys.lookup;

        if(this.presentation == PresentationKeys.lookup || !TagsTypeahead.presentInTeams){

            let renderedElement = initCustomRenderedElement("tikit-input tikit-typeahead");

            let options = this.displayChoices ?? [];
            options = options.map(o => { return { "label": o.title, "value": o.value}; })

            let selectedValues = [];
            if(this.defaultValue){
                const valuesArr = this.defaultValue.split(",");
                selectedValues = options.filter(o => valuesArr.includes(o.value));
            }

            const domNode = document.createElement('div');
            const root = createRoot(domNode); 

            root.render(<Select placeholder={AdaptiveCardTexts.SEARCH_WORD} options={options} isClearable={false} isMulti={this.isMultiSelectField} styles={colourStyles} defaultValue={selectedValues} {...TagsTypeahead.disableField && {isDisabled: true}}
                onChange={(newValue) => {
                    if(!Array.isArray(newValue))
                        newValue = [newValue];
                    const values = newValue.map(x => x["value"]);
                    this.setValue(TagsTypeahead.valueProperty, values.join());
                    handleValidationElement(renderedElement);
                    TagsTypeahead.onCustomValueChange && TagsTypeahead.onCustomValueChange(this);
                }}
            />);

            renderedElement.append(domNode);

            return renderedElement;

        }else
            return GetExpandedInputChoiceSet(this, this.isMultiSelectField);
    }

    get value(): string {
        return this.getValue(TagsTypeahead.valueProperty);
    }

    getJsonTypeName(): string {
        return TagsTypeahead.JsonTypeName;
    }

    updateLayout(processChildren: boolean = true) {
        super.updateLayout(processChildren);
    }
}

export class TagsTypeaheadPeer extends TypedCardElementPeer<TagsTypeahead> {
    static displayLabelProperty = getPropertyEditorInstance(TagsTypeahead.ElementId, PropertyKeys.displayLabel);
    static requireFieldProperty = getPropertyEditorInstance(TagsTypeahead.ElementId, PropertyKeys.requireField);
    static dataSourceProperty = getPropertyEditorInstance(TagsTypeahead.ElementId, PropertyKeys.dataSource, []);
    static presentationProperty = getPropertyEditorInstance(TagsTypeahead.ElementId, PropertyKeys.presentation);
    static isMultiSelectProperty = getPropertyEditorInstance(TagsTypeahead.ElementId, PropertyKeys.isMultiSelectField);

    static updateChoices(choices: IVersionedChoice[]) {
        this.dataSourceProperty = getPropertyEditorInstance(TagsTypeahead.ElementId, PropertyKeys.dataSource, choices);
    }
        
    populatePropertySheet(propertySheet: PropertySheet, defaultCategory: string = PropertySheetCategory.DefaultCategory) {
        
        super.populatePropertySheet(propertySheet, defaultCategory);
        removeOtherPropertyConfigurations(propertySheet);
        
        propertySheet.add(
            defaultCategory,
            TagsTypeaheadPeer.idProperty,
            TagsTypeaheadPeer.displayLabelProperty,
            TagsTypeaheadPeer.requireFieldProperty,
            TagsTypeaheadPeer.dataSourceProperty,
            TagsTypeaheadPeer.isMultiSelectProperty
        );

        SetTeamsExperiencePropertyConfigurations(propertySheet, [TagsTypeaheadPeer.presentationProperty]);
    }
}
